<template>
  <main-layout>
    <template #header>
      <ops-view-tabs />
    </template>

    <template #toolbar>
      <div class="filters">
        <v-text-field
          v-model="searchKeyword"
          dense
          hide-details
          clearable
          placeholder="업체명, 업체코드"
          style="max-width: 220px;"
          @keyup.enter="load"
        />
        <span class="period-label">기간</span>
        <div class="period-presets">
          <button
            v-for="preset in presets"
            :key="preset.key"
            type="button"
            class="period-chip"
            :class="{ active: periodKey === preset.key }"
            @click="applyPreset(preset.key)"
          >
            {{ preset.label }}
          </button>
        </div>
        <select v-model="dateType" class="period-select" @change="onDateTypeChange">
          <option value="M">매출월</option>
          <option value="R">등록일</option>
        </select>
        <input v-model="startInput" :type="dateType === 'M' ? 'month' : 'date'" class="period-date" @change="periodKey = 'custom'">
        <span class="period-tilde">~</span>
        <input v-model="endInput" :type="dateType === 'M' ? 'month' : 'date'" class="period-date" @change="periodKey = 'custom'">
        <select v-model="rankMetric" class="period-select" @change="onMetricChange">
          <option v-for="item in metrics" :key="item.value" :value="item.value">{{ item.text }}</option>
        </select>
        <label class="period-label">TOP</label>
        <input
          v-model.number="rankLimit"
          type="number"
          min="1"
          max="500"
          class="period-date top-input"
          :disabled="keywordOn"
          @keyup.enter="load"
        >
        <button type="button" class="period-chip" :class="{ active: reversed }" @click="reversed = !reversed">역순</button>
        <v-btn small color="primary" @click="load">조회</v-btn>
      </div>
    </template>

    <template #body>
      <div class="dashboard-scroll">
        <div v-if="errorMessage" class="error-box">{{ errorMessage }}</div>
        <div v-if="loading" class="loading-box">
          <v-progress-circular indeterminate color="primary" />
        </div>
        <section v-else class="panel">
          <header>
            <h3>{{ listTitle }}</h3>
            <span v-if="churn">기본은 모니터링이 없는 업체가 위입니다. 같으면 거래명세서·세금계산서·CMS 합이 적은 순입니다.</span>
            <span v-else>업체를 누르면 그 업체의 업무 현황으로 이동합니다.</span>
          </header>
          <div class="table-wrap">
            <table>
              <thead>
                <tr v-if="churn">
                  <th>순위</th>
                  <th>업체</th>
                  <th>모니터링</th>
                  <th>거래명세서</th>
                  <th>세금계산서</th>
                  <th>CMS</th>
                </tr>
                <tr v-else>
                  <th>순위</th>
                  <th>업체</th>
                  <th>{{ metricLabel }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="rows.length === 0">
                  <td :colspan="churn ? 6 : 3" class="empty">조회된 업체가 없습니다.</td>
                </tr>
                <tr v-for="(row, index) in rows" :key="row.rentalcompanyId" @click="openCompany(row)">
                  <td>{{ index + 1 }}</td>
                  <td>
                    <div class="name">{{ row.rentalcompanyName }}</div>
                    <div class="sub">{{ row.rentalcompanyId }} <span v-if="row.salesCustomCode">· {{ row.salesCustomCode }}</span></div>
                  </td>
                  <template v-if="churn">
                    <td>{{ count(row.monitoringCnt) }}</td>
                    <td>{{ count(row.stmtTotal) }}</td>
                    <td>{{ count(row.taxTotal) }}</td>
                    <td>{{ count(row.cmsTotal) }}</td>
                  </template>
                  <td v-else>{{ formatValue(row.rankValue) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </template>
  </main-layout>
</template>

<script>
import MainLayout from "@/components/common/layout/MainLayout";
import OpsViewTabs from "@/views/dashboard/OpsViewTabs";
import {PATH} from "@/constants";
import {DateTime} from "luxon";

export default {
  name: "CompanyRank",
  components: {OpsViewTabs, MainLayout},
  data() {
    return {
      loading: false,
      errorMessage: "",
      searchKeyword: "",
      periodKey: "30d",
      dateType: "M",
      startInput: DateTime.local().toFormat("yyyy-MM"),
      endInput: DateTime.local().toFormat("yyyy-MM"),
      rankMetric: "MONITORING",
      rankLimit: 10,
      reversed: false,
      rows: [],
      presets: [
        {key: "today", label: "오늘"},
        {key: "7d", label: "7일"},
        {key: "30d", label: "30일"},
        {key: "thisMonth", label: "이번달"},
        {key: "lastMonth", label: "지난달"},
      ],
      metrics: [
        {value: "MONITORING", text: "모니터링"},
        {value: "SALES", text: "매출"},
        {value: "BILLING", text: "청구 금액"},
        {value: "CONTRACT", text: "계약"},
        {value: "DEVICE", text: "장치"},
        {value: "PURCHASE", text: "매입"},
        {value: "CHURN", text: "해지위험"},
      ],
    }
  },
  computed: {
    metricLabel() {
      const found = this.metrics.find((item) => item.value === this.rankMetric)
      return found ? found.text : "모니터링"
    },
    churn() {
      return this.rankMetric === "CHURN"
    },
    keywordOn() {
      return !!(this.searchKeyword && String(this.searchKeyword).trim())
    },
    normalizedLimit() {
      const number = Number(this.rankLimit)
      if (!number || number < 1) return 10
      return Math.min(Math.floor(number), 500)
    },
    listTitle() {
      if (this.keywordOn) return this.metricLabel + " 검색 " + this.rows.length + "곳"
      const limit = this.normalizedLimit
      return this.metricLabel + (this.reversed ? " 하위 " : " TOP ") + limit
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    applyPreset(key) {
      this.periodKey = key
      const now = DateTime.local()
      if (this.dateType === "M") {
        const month = key === "lastMonth" ? now.minus({months: 1}) : now
        const text = month.toFormat("yyyy-MM")
        this.startInput = text
        this.endInput = text
      } else {
        let start = now.startOf("day")
        let end = now.startOf("day")
        if (key === "7d") start = now.minus({days: 6}).startOf("day")
        else if (key === "30d") start = now.minus({days: 29}).startOf("day")
        else if (key === "thisMonth") start = now.startOf("month")
        else if (key === "lastMonth") {
          start = now.minus({months: 1}).startOf("month")
          end = now.minus({months: 1}).endOf("month").startOf("day")
        }
        this.startInput = start.toFormat("yyyy-MM-dd")
        this.endInput = end.toFormat("yyyy-MM-dd")
      }
    },
    onMetricChange() {
      if (this.rankMetric === "CHURN") this.reversed = false
    },
    onDateTypeChange() {
      const key = this.periodKey === "custom" ? "30d" : this.periodKey
      this.applyPreset(key)
    },
    range() {
      if (this.dateType === "M") {
        const start = DateTime.fromFormat(this.startInput, "yyyy-MM")
        const end = DateTime.fromFormat(this.endInput, "yyyy-MM")
        return {
          start: (start.isValid ? start : DateTime.local()).startOf("month"),
          end: (end.isValid ? end : DateTime.local()).endOf("month"),
        }
      }
      const start = DateTime.fromISO(this.startInput)
      const end = DateTime.fromISO(this.endInput)
      return {
        start: (start.isValid ? start : DateTime.local()).startOf("day"),
        end: (end.isValid ? end : DateTime.local()).endOf("day"),
      }
    },
    async load() {
      const range = this.range()
      this.loading = true
      this.errorMessage = ""
      try {
        const res = await this.$request(PATH.DASHBOARD.RANK)
          .setObject({
            startDate: range.start.toISO({suppressMilliseconds: true}),
            endDate: range.end.toISO({suppressMilliseconds: true}),
            dateType: this.dateType,
            actualYn: "Y",
            searchKeyword: this.searchKeyword,
            rankMetric: this.rankMetric,
            rankLimit: this.normalizedLimit,
            descending: !this.reversed,
          })
          .catch()
          .enqueue()
        this.rows = Array.isArray(res) ? res : ((res && res.data) || [])
      } catch (e) {
        this.rows = []
        this.errorMessage = "업체 비교를 불러오지 못했습니다."
      } finally {
        this.loading = false
      }
    },
    openCompany(row) {
      this.$router.push({
        name: "managertnbox.dashboard",
        query: {
          rentalcompanyId: row.rentalcompanyId,
          name: row.rentalcompanyName,
          dateType: this.dateType,
          start: this.startInput,
          end: this.endInput,
          periodKey: this.periodKey,
        },
      })
    },
    count(value) {
      return Number(value || 0).toLocaleString("ko-KR")
    },
    formatValue(value) {
      const number = Number(value || 0)
      if (this.rankMetric === "SALES" || this.rankMetric === "PURCHASE" || this.rankMetric === "BILLING") {
        return number.toLocaleString("ko-KR", {maximumFractionDigits: 0})
      }
      return number.toLocaleString("ko-KR")
    },
  },
}
</script>

<style scoped>
.filters {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  width: 100%;
  padding: 4px 0 8px;
  gap: 8px;
}
.period-label, .period-tilde { color: #475467; font-size: 13px; }
.period-presets { display: flex; gap: 4px; }
.period-chip {
  border: 1px solid #d0d5dd;
  background: #fff;
  border-radius: 16px;
  padding: 4px 10px;
  font-size: 13px;
  cursor: pointer;
}
.period-chip.active {
  background: #0f766e;
  border-color: #0f766e;
  color: #fff;
}
.period-select, .period-date {
  border: 1px solid #d0d5dd;
  border-radius: 6px;
  height: 32px;
  padding: 0 8px;
  background: #fff;
}
.top-input { width: 72px; }
.top-input:disabled { background: #f2f4f7; color: #98a2b3; }
.dashboard-scroll { height: 100%; overflow-y: auto; }
.panel {
  background: #fff;
  border: 1px solid #eaecf0;
  border-radius: 12px;
  padding: 16px;
  text-align: left;
}
.panel header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 8px;
}
.panel h3 { margin: 0; font-size: 16px; }
.panel header span, .sub, .empty { color: #667085; font-size: 12px; }
.table-wrap { overflow-x: auto; }
table { width: 100%; border-collapse: collapse; }
th, td { border-bottom: 1px solid #eaecf0; padding: 10px 8px; text-align: left; font-size: 13px; }
tbody tr { cursor: pointer; }
tbody tr:hover { background: #f9fafb; }
.name { font-weight: 700; color: #1d2939; }
.error-box { color: #b42318; padding: 12px; text-align: left; }
.loading-box { display: flex; justify-content: center; padding: 48px; }
</style>
